import React, { useContext } from 'react';
import { Space, Divider, Button, Typography, Form, Modal, Alert } from 'antd';
import { Table, ApplicationUIContext, useModalState } from '@yuhuinc/ui-library';
import { useQueryClient } from '@tanstack/react-query';
import { DollarOutlined } from '@ant-design/icons';
import TitleWithIcon from '../common/TitleWithIcon';
import { formatMoney, toISO8601DateString } from 'helpers/Formatter';
import usePaysafe from '../common/usePaysafe';
import PaymentBlock from 'components/tenant_application/application_templates/wizard_steps/components/PaymentBlock';
import { apiPost } from 'helpers/ApiHelper';
import { getTranslatedString } from 'helpers/i18n';

const { Text, Paragraph } = Typography;

const APPLICANT_DEPOSITS_COLUMNS = [
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    render(value) {
      return formatMoney(value);
    },
  },
  {
    title: 'Paid',
    dataIndex: 'sent_at',
    key: 'sent_at',
    render(value) {
      return toISO8601DateString(value);
    },
  },
  {
    title: 'Paid by',
    dataIndex: 'cotenant_full_name',
    key: 'cotenant_full_name',
  },
];

const ApplicationPayments = ({ cotenant }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const queryClient = useQueryClient();
  const makePaymentModal = useModalState(false);
  const [paymentForm] = Form.useForm();
  const leasePricing = cotenant?.lease?.lease_pricing;
  const paymentDisabled =
    !leasePricing?.payment_details?.split_pay ||
    leasePricing?.payment_details?.required ||
    cotenant.lease.lease_pricing.deposit_owing <= 0;

  const makePayment = async (result, paymentDetails) => {
    await apiPost(`/leads/application_portal/cotenants/${cotenant.id}/payments/make_cc_payment`, {
      token: result.token,
      payment_amount: paymentDetails['payment_amount'],
      name: paymentDetails['name'],
      postal_code: paymentDetails['postal_code'],
    });
    makePaymentModal.close();
    queryClient.invalidateQueries({ queryKey: ['cotenant', cotenant.id] });

    Modal.success({
      title: 'Success!',
      content: 'Your payment has been accepted.',
      okText: getTranslatedString('okay'),
    });
  };

  const {
    isPaying,
    cardNumberError,
    cvvError,
    expiryDateError,
    paymentError,
    setPaysafeInstance,
    setCardNumberError,
    setCvvError,
    setExpiryDateError,
    submitPayment,
  } = usePaysafe({ paymentForm, onPaymentTokenizeSuccess: makePayment });

  if (!cotenant?.lease?.lease_pricing) return null;

  const payments = cotenant.lease.lease_pricing.batch_payments;

  return (
    <>
      <Divider />
      <TitleWithIcon icon={<DollarOutlined />} text="Payments" />

      <Text>Balance: {formatMoney(cotenant.lease.lease_pricing.deposit_owing)}</Text>

      <Space size="middle" direction="vertical" style={{ width: '100%' }}>
        {payments.length > 0 ? (
          <Table
            dataSource={payments}
            columns={APPLICANT_DEPOSITS_COLUMNS}
            hideActionAndSearch
            size={isMobile ? 'middle' : 'small'}
            scroll={{
              x: isMobile,
            }}
            pagination={{ hideOnSinglePage: true }}
            rowKey="id"
          />
        ) : null}
        <Button
          type="default"
          size="medium"
          disabled={paymentDisabled}
          loading={isPaying}
          onClick={makePaymentModal.open}
        >
          Make Payment
        </Button>
      </Space>
      <Modal
        visible={makePaymentModal.isVisible}
        destroyOnClose
        title="Make a Payment"
        onCancel={makePaymentModal.close}
        footer={[
          <Button key="submit" type="primary" onClick={submitPayment} loading={isPaying}>
            Pay
          </Button>,
        ]}
      >
        {paymentError && <Alert message={paymentError} type="error" showIcon />}
        <Paragraph>
          Payments are made for everyone on the application, the amount you choose to pay will be
          deducted from the group’s balance.{' '}
        </Paragraph>
        <Paragraph strong>
          Balance: {formatMoney(cotenant.lease.lease_pricing.deposit_owing)}
        </Paragraph>
        <PaymentBlock
          form={paymentForm}
          setPaysafeInstance={setPaysafeInstance}
          cardNumberError={cardNumberError}
          cvvError={cvvError}
          expiryDateError={expiryDateError}
          setCardNumberError={setCardNumberError}
          setCvvError={setCvvError}
          setExpiryDateError={setExpiryDateError}
          paymentDisabled={paymentDisabled}
          paymentHold={leasePricing?.payment_details?.hold}
        />
      </Modal>
    </>
  );
};

export default ApplicationPayments;
